
import { mixins } from "vue-class-component";
import { DateUtilsMixin } from "@/mixins/date-utils-mixin";
import { Getter, State } from 'vuex-class';
import { Component, Vue } from 'vue-property-decorator';
import { TableConfig } from '@/types';
import SubSection from '@/components/shared/SubSection.vue';
import { NowListItem } from '@/store/deceasedDonors/types';
import { NumericCodeValue } from '@/store/types';
import { OrganCodeValue } from '@/store/lookups/types';
import { Hospital } from '@/store/hospitals/types';

interface NowListRow {
  facility_name: string;
  national_recipient_id: string;
  local_recipient_id: string;
  transplant_type: string;
  organ: string;
  list_date: string;
  organ_medical_status: string;
  status: string;  //HSP or HSH
  organ_request_status: string;
  blood_group: string;
  age: number;
  height: number;
  weight: number;
  organ_cpra: number;
  comments: string
}

@Component({
  components: {
    SubSection,
  }
})
export default class NationalOrganWaitlistTable extends mixins(DateUtilsMixin) {
  @State(state => state.pageState.currentPage) private currentPage!: any;
  @State(state => state.pageState.currentPage.nowLists) private editState!: any;
  @State(state => state.deceasedDonors.nowLists) nowLists!: NowListItem[];

  @Getter('organOptions', { namespace: 'lookups' }) organOptions!: (type?: string) => NumericCodeValue[];
  @Getter('getHospitalByClientId', { namespace: 'hospitals' }) private getHospitalByClientId!: (clientId?: string|null) => Hospital|null;

  public currentPageTable = 1;
  public perPage = 10;
  private loading = false;

  /**
   * Getter for the columns.
   *
   */
  get nowListColumns(): any[] {
    return [
        {
          label: `${this.$t('odo')}`,
          field: 'facility_name',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('national_patient_id')}`,
          field: 'national_recipient_id',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('local_id')}`,
          field: 'local_recipient_id',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('tx_type')}`,
          field: 'transplant_type',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('organ')}`,
          field: 'organ',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('list_date')}`,
          field: 'list_date',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('status')}`,
          field: 'status',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: ' ',
            type: 'text'
          }
        },
        {
          label: `${this.$t('medical_status')}`,
          field: 'organ_medical_status',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('state')}`,
          field: 'organ_request_status',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('abo')}`,
          field: 'blood_group',
          sortable: true,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('age')}`,
          field: 'age',
          sortFn: this.sortBy,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('height')} ${this.$t('height_measurement')}`,
          field: 'height',
          sortFn: this.sortBy,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('weight')} ${this.$t('weight_measurement')}`,
          field: 'weight',
          sortFn: this.sortBy,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('cpra')}`,
          field: 'organ_cpra',
          sortFn: this.sortBy,
          filterOptions: {
            enabled: true,
            custom: true,
            type: 'text'
          }
        },
        {
          label: `${this.$t('comments')}`,
          field: 'comments',
          sortable: false,
          filterOptions: {
            enabled: false,
            custom: false,
            type: 'text'
          }
        },
      ];
  }

  /**
   * Get table configuration details for the subsection
   *
   * @returns {TableConfig} Now List table configuration
   */
  get nowListTableConfig(): TableConfig {
    return {
      data: this.nowListRows,
      columns: this.nowListColumns,
      empty: this.emptyMessage,
      pagination: true,
      sortOptions: {
        enabled: true,
        initialSortBy: {field: 'national_recipient_id', type: 'asc'}
      },
      paginationOptions: {
        enabled: true,
        mode: 'records',
        perPage: this.perPage,
        perPageDropdown: [10, 25, 50],
        setCurrentPage: this.currentPageTable,
        dropdownAllowAll: true,
        nextLabel: this.$t('table.older'),
        prevLabel: this.$t('table.newer'),
        rowsPerPageLabel: this.$t('table.results'),
      },
    };
  }

  // Show loading state or empty message
  get emptyMessage(): string {
    if (this.loading) {
      return `${this.$t('loading')}`;
    } else {
      return `${this.$t('empty_now_lists')}`;
    }
  }

  /**
   * Get all rows for table data
   *
   * @returns {NowListRow[]} Now List data
   */
  get nowListRows(): NowListRow[] {

    // Our state isn't set yet
    if (!this.editState || !this.editState.nowLists) return [];
    // We have no data in the nowlists
    if (this.editState.nowLists.length <= 0) return [];


    const results: NowListRow[] = [];

    this.editState.nowLists.forEach((record: NowListItem) => {
      const acceptance_criteria = this.buildComments(record.acceptance_criteria);
      const hospital = this.getHospitalByClientId(record.opo.hospital_code);
      const facility_name = hospital?.program_identifier || record.opo.facility_code;

      const row: NowListRow = {
        facility_name: facility_name,
        national_recipient_id: record.national_recipient_id,
        local_recipient_id: record.local_recipient_id,
        transplant_type: record.transplant_type,
        organ: record.organ,
        list_date: this.parseDateUiFromDateTime(record.list_date) || '',
        status: this.calculateStatus(record),
        organ_medical_status: record.organ_medical_status,
        organ_request_status: record.organ_request_status,
        blood_group: record.blood_group || '-',
        age: record.age.years,
        height: record.height,
        weight: record.weight,
        organ_cpra: record.organ_cpra,
        comments: acceptance_criteria.join(', ')

      };
      results.push(row);
    });
    return results;
  }

  public calculateStatus(record: NowListItem) {
    const hspEligibile = [OrganCodeValue.Kidney].includes(Number(record.organ_code)) && record.hsx_eligible;
    const hshEligibile = [OrganCodeValue.Heart].includes(Number(record.organ_code)) && record.hsx_eligible;
    let statuses: string[] = [];

    if(hspEligibile) {
      statuses.push('HSP');
    }

    if(hshEligibile) {
      statuses.push('HSH');
    }

    if(record.high_status) {
      statuses.push('HS');
    }
    return statuses.join(',');
  }

  public buildComments(acceptance_criteria: any){
    let comments: string[] = [];
    Object.keys(acceptance_criteria).map((key: string) => {
      const value = acceptance_criteria[key];
      const type = typeof value;
      if (value) comments.push(type === 'boolean' ? key.replace(/_/g, ' ') : `${key.replace(/_/g, ' ')} = ${value}`);
    });
    return comments;
  }

  /**
   * Sets the search terms
   *
  */
  public filterList(event: any) {
    let search_params = event?.searchParams;

    let filteredList = this.filterNowList(search_params);

   if(filteredList) {
    Vue.set(this.editState, 'nowLists', filteredList);
   }
  }

  public filterNowList(search_params: { [x: string]: string; }) {
    let filteredList = this.nowLists;

    Object.keys(search_params).map((key:string) => {
      if(search_params[key]!= ""){
        switch(key) {
          case 'transplant_type':
            filteredList = filteredList.filter(list => list.transplant_type.toLowerCase().includes(search_params[key].toLowerCase()));
            break;
          case 'national_recipient_id':
            filteredList = filteredList.filter(list => list.national_recipient_id.toLowerCase().includes(search_params[key].toLowerCase()));
            break;
          case 'local_recipient_id':
            filteredList = filteredList.filter(list => list.local_recipient_id.includes(search_params[key]));
            break;
          case 'facility_name':
            filteredList = filteredList.filter((list) => {
              const hospital = this.getHospitalByClientId(list.opo.hospital_code);
              const compareName = hospital?.program_identifier || list.opo.facility_code;
              return compareName.toLowerCase().includes(search_params[key].toLowerCase())
            });
            break;
          case 'organ':
            filteredList = filteredList.filter(list => list.organ.toLowerCase().includes(search_params[key].toLowerCase()));
            break;
          case 'list_date':
            filteredList = filteredList.filter(list => list.list_date.toLowerCase().includes(search_params[key].toLowerCase()));
            break;
          case 'status':
            filteredList = filteredList.filter(list => list.hsx_eligible == (['hsp','hs'].includes(search_params[key].toLowerCase()) ? true : false));            break;
          case 'organ_medical_status':
            filteredList = filteredList.filter(list => list.organ_medical_status.includes(search_params[key]));
            break;
          case 'organ_request_status':
            filteredList = filteredList.filter(list => list.organ_request_status.toLowerCase().includes(search_params[key].toLowerCase()));
            break;
          case 'blood_group':
            filteredList = filteredList.filter(list => (list.blood_group ? list.blood_group.toLowerCase() : '-') == search_params[key]);
            break;
          case 'age':
            filteredList = filteredList.filter(list => list.age.years.toString() == search_params[key]);
            break;
          case 'height':
            filteredList = filteredList.filter(list => list.height.toString() == search_params[key]);
            break;
          case 'weight':
            filteredList = filteredList.filter(list => list.weight.toString() == search_params[key]);
            break;
          case 'organ_cpra':
            filteredList = filteredList.filter(list => list.organ_cpra.toString() == search_params[key]);
            break;
          default:
            break;
        }
      }
    });
	  return filteredList;
  }

  public loadData(payload = {})  {
    Vue.set(this.currentPage, 'nowLists', { nowLists: null });
    this.loading = true;
    this.$store.dispatch('hospitals/load');

    this.$store.dispatch('deceasedDonors/getNowList', {
       search_params: payload,
    }).then(()=>{
      Vue.set(this.editState, 'nowLists', this.nowLists);
      this.loading = false;
      this.$emit('nowListLoaded');
    }).catch(() => {
      this.loading = false;
      this.$emit('nowListError');
    });
  }

  private sortBy(aValue: string, bValue: string, column: any, aRow: any, bRow: any): number {
    const aNumber = aRow[column.field] || 0;
    const bNumber = bRow[column.field] || 0;

    // Sort numerically and return
    return Math.sign(aNumber - bNumber);
  }
}
